<div class="modal-overlay" (click)="closeModal()">
  <app-loading-modal
    [show]="loading"
    [message]="mode === 'signup' ? 'Creating Account...' : mode === 'reset' ? 'Sending link...' : 'Signing In...'">
  </app-loading-modal>

  <div class="modal-content" @modalAnimation (click)="$event.stopPropagation()">
    <button class="close-button" type="button" (click)="closeModal()" aria-label="Close sign in dialog">
      <i class="bi bi-x-lg" aria-hidden="true"></i>
    </button>

    @if (mode === 'mfa' && mfaResolver) {
      <app-mfa-challenge
        [resolver]="mfaResolver"
        (verified)="onMfaVerified()"
        (cancelled)="cancelMfa()">
      </app-mfa-challenge>
    } @else {
      <div class="modal-header">
        <h2 class="modal-title">
          {{ mode === 'signup' ? 'Create Account' : mode === 'reset' ? 'Reset Password' : 'Welcome Back' }}
        </h2>
        <p class="modal-subtitle">
          {{ mode === 'signup' ? 'Join the Restless Society community'
            : mode === 'reset' ? "We'll email you a link to choose a new password"
            : 'Sign in to your account' }}
        </p>
      </div>

      @if (mode !== 'reset') {
        <button type="button" class="google-button" [disabled]="loading" (click)="signInWithGoogle()">
          <svg class="google-icon" viewBox="0 0 48 48" aria-hidden="true" width="18" height="18">
            <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/>
            <path fill="#FBBC05" d="M10.5 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.1z"/>
            <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.9 2.3-8.4 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
          </svg>
          {{ mode === 'signup' ? 'Sign up with Google' : 'Continue with Google' }}
        </button>
        <div class="or-divider"><span>or use email</span></div>
      }

      <form [formGroup]="authForm" (ngSubmit)="onSubmit()" class="auth-form">
        @if (mode === 'signup') {
          <div class="form-group">
            <input
              type="text"
              id="displayName"
              formControlName="displayName"
              class="form-input"
              placeholder="Display Name (Optional)"
              autocomplete="name"
              maxlength="60"
            />
            <label for="displayName" class="form-label">Display Name (Optional)</label>
          </div>
        }

        <div class="form-group">
          <input
            type="email"
            id="email"
            formControlName="email"
            class="form-input"
            placeholder="your@email.com"
            autocomplete="email"
          />
          <label for="email" class="form-label">Email</label>
          @if (shouldShowError('email')) {
            <div class="field-error">{{ getErrorMessage('email') }}</div>
          }
        </div>

        @if (mode !== 'reset') {
          <div class="form-group">
            <div class="input-field-container">
              <input
                [type]="showPassword ? 'text' : 'password'"
                id="password"
                formControlName="password"
                class="form-input"
                placeholder="Enter your password"
                maxlength="128"
                [attr.autocomplete]="mode === 'signup' ? 'new-password' : 'current-password'"
              />
              <label for="password" class="form-label">Password</label>
              <button type="button" class="toggle-password" (click)="togglePasswordVisibility()" [attr.aria-label]="showPassword ? 'Hide password' : 'Show password'" [attr.aria-pressed]="showPassword">
                <i class="bi" [class.bi-eye]="!showPassword" [class.bi-eye-slash]="showPassword"></i>
              </button>
            </div>
            @if (shouldShowError('password') && mode === 'signin') {
              <div class="field-error">{{ getErrorMessage('password') }}</div>
            }
          </div>

          @if (mode === 'signup') {
            <ul class="password-rules" aria-live="polite">
              @for (check of passwordChecks; track check.rule.id) {
                <li [class.met]="check.met">
                  <i class="bi" [class.bi-check-circle-fill]="check.met" [class.bi-circle]="!check.met" aria-hidden="true"></i>
                  {{ check.rule.label }}
                </li>
              }
            </ul>
            <!-- <p class="password-note">We also check new passwords against known data breaches.</p> -->
          }
        }

        @if (errorMessage) {
          <div class="error-message" role="alert">{{ errorMessage }}</div>
        }

        @if (successMessage) {
          <div class="success-message" role="status">{{ successMessage }}</div>
        }

        <button type="submit" class="submit-button" [disabled]="!canSubmit">
          {{ mode === 'signup' ? 'Sign Up' : mode === 'reset' ? 'Send reset link' : 'Sign In' }}
        </button>

        @if (mode === 'signin') {
          <button type="button" class="link-button" (click)="setMode('reset')">Forgot password?</button>
        }
      </form>

      <div class="modal-footer">
        @if (mode === 'reset') {
          <button type="button" class="toggle-button" (click)="setMode('signin')">← Back to sign in</button>
        } @else {
          <p class="toggle-text">
            {{ mode === 'signup' ? 'Already have an account?' : "Don't have an account?" }}
            <button type="button" class="toggle-button" (click)="toggleMode()">
              {{ mode === 'signup' ? 'Sign In' : 'Sign Up' }}
            </button>
          </p>
        }
      </div>
    }
  </div>
</div>
